import { chatbotCdnFallback, chatbotWsBaseDomain, helpCenterUrl } from '@/localized/config/site';
import '@/public/iconfont/iconfont.css';
import { BlofinProvider, LanguagesType } from '@blofin/blofin-component';
import { NoticeProvider, ThemeProvider } from '@blofin/blofin-ui';
import { ChatbotGrayGate, type ChatbotEnv } from '@blofin/blofin-utils';
import SEO from 'config/next-seo.config';
import useUserInfo from 'hooks/useUserInfo';
import Cookies from 'js-cookie';
import { NextIntlClientProvider } from 'next-intl';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import apiConfig, { baseUrl, navUrl } from 'services/http/base';
import store from 'store/store';
import { selectMode } from 'store/theme/themeSlice';
import 'styles/globals.css';
import 'styles/normalize.css';

const chatbotEnv: ChatbotEnv = {
  baseUrl,
  navUrl,
  getToken: () => Cookies.get('token') || null,
  helpCenterUrl,
  wsBaseDomain: chatbotWsBaseDomain,
  cdnFallback: chatbotCdnFallback,
};

function GlobalNoticeProvider({ children, locale }: { children: React.ReactNode; locale?: string }) {
  const mode = useSelector(selectMode);
  const direction = locale === 'ar' ? 'rtl' : 'ltr';
  return (
    <ThemeProvider value={{ theme: mode, direction }}>
      <NoticeProvider>{children}</NoticeProvider>
    </ThemeProvider>
  );
}

function SupportChatbotGrayGate() {
  const mode = useSelector(selectMode);
  const themeRef = useRef<'dark' | 'light' | null>(null);
  if (themeRef.current === null) {
    const domTheme =
      typeof document !== 'undefined'
        ? (document.documentElement.getAttribute('data-theme') as 'dark' | 'light' | null)
        : null;
    themeRef.current = domTheme === 'dark' || domTheme === 'light' ? domTheme : mode === 'light' ? 'light' : 'dark';
  }
  return <ChatbotGrayGate theme={themeRef.current} env={chatbotEnv} />;
}

function MyApp({ Component, pageProps }: AppProps) {
  const userInfo = useUserInfo();

  const { locale, asPath } = useRouter();

  const data = useMemo(() => {
    return {
      userInfo: userInfo,
      baseUrl: apiConfig.baseUrl,
      lang: locale as LanguagesType,
      messages: pageProps.messages ?? {},
      rates: undefined,
    } as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  useEffect(() => {
    window.React = React;
    window.ReactDOM = ReactDOM;
    (window as any).__BUILD_INFO = {
      buildNumber: process.env.NEXT_PUBLIC_BUILD_NUMBER,
    };
  }, []);

  useEffect(() => {
    if (userInfo !== null) {
      if (userInfo.account_type === 'NORMAL_SUB_ACCOUNT') {
        Cookies.remove('Global-UserViewType');
      }
      Cookies.set('Global-AccountType', userInfo.account_type);
      window.userInfo = userInfo;
    }
  }, [userInfo]);

  return (
    <>
      <DefaultSeo {...SEO(locale as string, asPath)} />
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" />
      </Head>
      <Provider store={store}>
        <BlofinProvider value={data}>
          <GlobalNoticeProvider locale={locale}>
            <SupportChatbotGrayGate />
            <NextIntlClientProvider
              locale={locale}
              messages={pageProps.messages}
              getMessageFallback={({ namespace, key }) => {
                return `${namespace ? `${namespace}.` : ''}${key}`;
              }}
            >
              <Component {...pageProps} />
            </NextIntlClientProvider>
          </GlobalNoticeProvider>
        </BlofinProvider>
      </Provider>
    </>
  );
}

export default MyApp;
